import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

type ActivityType = 
  | "calculator_started"
  | "calculator_completed"
  | "calculator_interaction"
  | "calculator_email_submitted";

interface TrackingMetadata {
  calculator_type: string;
  [key: string]: unknown;
}

interface UseCalculatorTrackingOptions {
  calculatorType: string;
}

export const useCalculatorTracking = ({ calculatorType }: UseCalculatorTrackingOptions) => {
  const hasTrackedStart = useRef(false);
  const leadEmailRef = useRef<string | null>(null);

  const trackActivity = useCallback(async (
    activityType: ActivityType,
    metadata: Partial<TrackingMetadata> = {}
  ) => {
    try {
      const payload = {
        email: leadEmailRef.current || undefined,
        activity_type: activityType,
        activity_metadata: {
          calculator_type: calculatorType,
          timestamp: new Date().toISOString(),
          ...metadata,
        },
      };

      // Only track if we have an email (lead is identified)
      if (!payload.email) {
        console.log(`[Calculator Tracking] No email set, skipping ${activityType}`);
        return;
      }

      const { error } = await supabase.functions.invoke("track-lead-activity", {
        body: payload,
      });

      if (error) {
        console.error("[Calculator Tracking] Error:", error);
      } else {
        console.log(`[Calculator Tracking] Tracked: ${activityType}`);
      }
    } catch (err) {
      console.error("[Calculator Tracking] Unexpected error:", err);
    }
  }, [calculatorType]);

  const setLeadEmail = useCallback((email: string) => {
    leadEmailRef.current = email;
  }, []);

  const trackStart = useCallback(() => {
    if (!hasTrackedStart.current && leadEmailRef.current) {
      hasTrackedStart.current = true;
      trackActivity("calculator_started");
    }
  }, [trackActivity]);

  const trackCompletion = useCallback((results: Record<string, unknown>) => {
    trackActivity("calculator_completed", { results });
  }, [trackActivity]);

  const trackInteraction = useCallback((interactionType: string, value?: unknown) => {
    trackActivity("calculator_interaction", { 
      interaction_type: interactionType,
      value,
    });
  }, [trackActivity]);

  const trackEmailSubmission = useCallback((email: string, calculatorData: Record<string, unknown>) => {
    leadEmailRef.current = email;
    trackActivity("calculator_email_submitted", { 
      email,
      calculator_inputs: calculatorData,
    });
  }, [trackActivity]);

  return {
    setLeadEmail,
    trackStart,
    trackCompletion,
    trackInteraction,
    trackEmailSubmission,
  };
};
